import moment from 'moment';
import { useRef, useState, useEffect, useCallback, KeyboardEvent } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import {
  AssistantEvent,
  AssistantAction,
  streamAssistant,
  AssistantConversation,
  useGetAssistantConversationsQuery,
  useLazyGetAssistantConversationQuery,
  useDeleteAssistantConversationMutation,
} from 'src/services/ai/ai-assistant';

import Iconify from 'src/components/iconify';

import AssistantMarkdown from './assistant-markdown';
import AssistantActionCard from './assistant-action-card';

export interface Turn {
  id: string;
  question: string;
  answer: string;
  tools: { id: string; label: string; status: 'running' | 'done' | 'failed' }[];
  actions: AssistantAction[];
  state: 'streaming' | 'done' | 'error';
  error?: string;
}

const STARTERS = [
  { icon: 'solar:phone-calling-bold', label: 'Who should I follow up today?' },
  { icon: 'solar:calendar-bold', label: 'What interviews do I have this week?' },
  { icon: 'solar:dollar-bold', label: 'Which of my deals have gone quiet?' },
  { icon: 'solar:users-group-rounded-bold', label: 'Find candidates available now for my live jobs' },
  { icon: 'solar:checklist-minimalistic-bold', label: 'What tasks are overdue?' },
];

let turnSeq = 0;
const newTurnId = () => {
  turnSeq += 1;
  return `turn-${turnSeq}`;
};

export function streamErrorText(event: Extract<AssistantEvent, { type: 'error' }>): string {
  if (event.message) return event.message;
  if (event.status === 402 || event.status === 403) return 'The assistant is part of the AI layer, which this agency does not have.';
  if (event.status === 401) return 'Your session has ended. Refresh the page and sign in again.';
  return 'The assistant could not answer just now. Try again in a moment.';
}

/**
 * Turns a saved conversation into the window's turns: each question with the answer after it, and
 * each proposed change on the answer it came with (the first answer saved after it).
 */
export function turnsFrom(conversation: AssistantConversation): Turn[] {
  const turns: (Turn & { answeredAt: number })[] = [];
  conversation.messages.forEach((message) => {
    if (message.role === 'user') {
      turns.push({ id: message.id, question: message.text, answer: '', tools: [], actions: [], state: 'done', answeredAt: Infinity });
      return;
    }
    const turn = turns[turns.length - 1];
    if (!turn || turn.answer) {
      turns.push({ id: message.id, question: '', answer: message.text, tools: [], actions: [], state: 'done', answeredAt: Date.parse(message.created_at) });
    } else {
      turn.answer = message.text;
      turn.answeredAt = Date.parse(message.created_at);
    }
    const last = turns[turns.length - 1];
    last.tools = message.tools_used.map((tool, i) => ({ id: `${message.id}-${i}`, label: tool.label, status: 'done' as const }));
  });
  conversation.actions.forEach((action) => {
    const at = Date.parse(action.created_at);
    const turn = turns.find((candidate) => candidate.answeredAt >= at) ?? turns[turns.length - 1];
    turn?.actions.push(action);
  });
  return turns.map(({ answeredAt, ...turn }) => turn);
}

function applyEvent(turn: Turn, event: AssistantEvent): Turn {
  switch (event.type) {
    case 'text':
      return { ...turn, answer: turn.answer + event.delta };
    case 'tool': {
      const others = turn.tools.filter((tool) => tool.id !== event.id);
      return { ...turn, tools: [...others, { id: event.id, label: event.label, status: event.status }] };
    }
    case 'action':
      return { ...turn, actions: [...turn.actions, event.action] };
    case 'done':
      return { ...turn, state: 'done', answer: turn.answer || event.text };
    case 'error':
      return { ...turn, state: 'error', error: streamErrorText(event) };
    default:
      return turn;
  }
}

function ToolLine({ tools, streaming }: { tools: Turn['tools']; streaming: boolean }) {
  if (!tools.length) return null;
  const running = tools.filter((tool) => tool.status === 'running');
  if (streaming && running.length) {
    return (
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">{running.map((tool) => tool.label).join(', ')}...</Typography>
      </Stack>
    );
  }
  const labels = tools.map((tool) => tool.label).filter((label, i, all) => all.indexOf(label) === i);
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
      {labels.map((label) => (
        <Chip
          key={label}
          size="small"
          variant="outlined"
          label={label}
          icon={<Iconify icon={tools.some((tool) => tool.label === label && tool.status === 'failed') ? 'solar:danger-circle-linear' : 'solar:check-circle-linear'} width={14} />}
          sx={{ height: 20, fontSize: 11, color: 'text.secondary' }}
        />
      ))}
    </Stack>
  );
}

/**
 * The CRM assistant's chat window (JT1-1779). Answers stream in as they are written, it says what
 * it is looking up while it does, past conversations are listed and can be reopened and continued,
 * and links in answers open the record. Changes it proposes appear as cards to confirm (JT1-1780).
 */
export default function AssistantChatPanel({ onClose }: { onClose: () => void }) {
  const theme = useTheme();
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [opening, setOpening] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { currentData: conversations, isFetching: loadingList, refetch: refetchList } = useGetAssistantConversationsQuery(undefined, { skip: view !== 'history' });
  const [loadConversation] = useLazyGetAssistantConversationQuery();
  const [deleteConversation] = useDeleteAssistantConversationMutation();

  const streaming = turns.some((turn) => turn.state === 'streaming');

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [turns]);

  const updateTurn = useCallback((id: string, update: (turn: Turn) => Turn) => {
    setTurns((current) => current.map((turn) => (turn.id === id ? update(turn) : turn)));
  }, []);

  const ask = async (text: string) => {
    const question = text.trim();
    if (!question || streaming) return;
    setInput('');
    const id = newTurnId();
    setTurns((current) => [...current, { id, question, answer: '', tools: [], actions: [], state: 'streaming' }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamAssistant({ message: question, conversationId, signal: controller.signal }, (event) => {
        if (event.type === 'conversation') setConversationId(event.id);
        updateTurn(id, (turn) => applyEvent(turn, event));
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        updateTurn(id, (turn) => ({ ...turn, state: 'done', answer: turn.answer || '_Stopped._' }));
      } else {
        updateTurn(id, (turn) => applyEvent(turn, { type: 'error', status: 0 }));
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const stop = () => abortRef.current?.abort();

  const startNew = () => {
    abortRef.current?.abort();
    setTurns([]);
    setConversationId(null);
    setView('chat');
  };

  const open = async (id: string) => {
    abortRef.current?.abort();
    setOpening(true);
    try {
      const conversation = await loadConversation(id).unwrap();
      setTurns(turnsFrom(conversation));
      setConversationId(conversation.id);
      setView('chat');
    } catch {
      refetchList();
    } finally {
      setOpening(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteConversation(id).unwrap();
    } catch {
      // Already gone; the list refetches either way.
    }
    if (id === conversationId) startNew();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(input);
    }
  };

  const setAction = (turnId: string, action: AssistantAction) =>
    updateTurn(turnId, (turn) => ({ ...turn, actions: turn.actions.map((existing) => (existing.id === action.id ? action : existing)) }));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: '#078DEE', color: '#fff', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box component="img" src="/assets/ai/linki-headshot.svg" sx={{ width: 28, height: 28, borderRadius: '50%' }} />
          <Typography variant="subtitle2">Linki</Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          <Tooltip title={view === 'history' ? 'Back to the conversation' : 'Past conversations'}>
            <IconButton size="small" onClick={() => setView(view === 'history' ? 'chat' : 'history')} sx={{ color: 'inherit' }} aria-label="Past conversations">
              <Iconify icon={view === 'history' ? 'solar:chat-round-line-bold' : 'solar:history-bold'} width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={startNew} sx={{ color: 'inherit' }} aria-label="New conversation">
              <Iconify icon="solar:pen-new-square-bold" width={18} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }} aria-label="Close">
            <Iconify icon="solar:alt-arrow-right-bold" width={18} />
          </IconButton>
        </Stack>
      </Stack>

      {view === 'history' ? (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {(loadingList || opening) && <Stack alignItems="center" py={3}><CircularProgress size={20} /></Stack>}
          {!loadingList && conversations?.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>No past conversations yet.</Typography>
          )}
          {!loadingList && conversations?.map((conversation) => (
            <Stack
              key={conversation.id}
              direction="row"
              alignItems="center"
              gap={1}
              onClick={() => open(conversation.id)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: conversation.id === conversationId ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{conversation.title}</Typography>
                <Typography variant="caption" color="text.secondary">{moment(conversation.last_message_at).fromNow()}</Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Delete conversation"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(conversation.id);
                }}
              >
                <Iconify icon="solar:trash-bin-trash-linear" width={16} />
              </IconButton>
            </Stack>
          ))}
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {turns.length === 0 && (
            <Stack gap={1.5} mt={2}>
              <Stack alignItems="center" gap={1} mb={1}>
                <Box component="img" src="/assets/ai/linki-headshot.svg" sx={{ width: 56, height: 56 }} />
                <Typography variant="subtitle1" fontWeight={700} textAlign="center">Hi, I&apos;m Linki</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Ask me about your candidates, jobs, contacts, deals, tasks and reports. I can also add tasks and notes and move
                  candidates, once you confirm.
                </Typography>
              </Stack>
              {STARTERS.map(({ icon, label }) => (
                <Box
                  key={label}
                  onClick={() => ask(label)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#2DD3BE18' },
                  }}
                >
                  <Iconify icon={icon} width={20} sx={{ color: '#6466F1', flexShrink: 0 }} />
                  <Typography variant="body2">{label}</Typography>
                </Box>
              ))}
            </Stack>
          )}

          {turns.map((turn) => (
            <Box key={turn.id} data-testid="assistant-turn">
              {turn.question && (
                <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                  <Box sx={{ maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2, bgcolor: '#005AD2', color: '#fff' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{turn.question}</Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
                <Box sx={{ maxWidth: '92%', minWidth: 60, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'background.neutral' }}>
                  <ToolLine tools={turn.tools} streaming={turn.state === 'streaming'} />
                  {turn.answer && <AssistantMarkdown text={turn.answer} />}
                  {turn.state === 'streaming' && !turn.answer && !turn.tools.some((tool) => tool.status === 'running') && <CircularProgress size={14} />}
                  {turn.actions.map((action) => (
                    <AssistantActionCard key={action.id} action={action} onChange={(next) => setAction(turn.id, next)} />
                  ))}
                  {turn.state === 'error' && (
                    <Typography variant="body2" color="error.main" sx={{ mt: turn.answer ? 0.75 : 0 }}>{turn.error}</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>
      )}

      {view === 'chat' && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <InputBase
            fullWidth
            multiline
            maxRows={4}
            placeholder={conversationId ? 'Ask a follow-up...' : 'Ask anything...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            sx={{ fontSize: 14 }}
            inputProps={{ 'aria-label': 'Message' }}
          />
          {streaming ? (
            <Button size="small" color="inherit" onClick={stop} startIcon={<Iconify icon="solar:stop-bold" width={16} />}>Stop</Button>
          ) : (
            <IconButton onClick={() => ask(input)} sx={{ color: '#005AD2' }} disabled={!input.trim()} size="small" aria-label="Send">
              <Iconify icon="solar:plain-bold" width={20} />
            </IconButton>
          )}
        </Stack>
      )}
    </Box>
  );
}
